import React, { useEffect, useMemo, useState } from 'react';
import { DEFAULT_MODELS, PROVIDER_LABELS } from '../../utils/modelConstants';
import { defaultModelFor } from '../../utils/modelLists';
import { isUsableProviderConfig } from '../../utils/userPageHelpers';
import { defaultChoiceLabel } from '../../utils/workflowModelSlots';
import ModelSelect from './ModelSelect';
import { Field, FieldAlert, TypeHint } from './workflowFormFields';
import {
  STACKFORM_PROVIDER_OPTION,
  buildConfiguredProviderOptions,
} from './workflowProviderOptions';

/**
 * The model a newly picked provider key starts with ('' when it has
 * none): the recommended one in its model field's list, else the first.
 */
function initialModel({ value: providerKey, provider }, modelLists = {}) {
  if (provider === 'stackform') return DEFAULT_MODELS.default || '';
  return defaultModelFor(providerKey, modelLists) || '';
}

/** A draft for a stored choice (or for none). */
function draftFrom(choice) {
  return {
    provider: choice?.provider || '',
    configIndex: choice?.config_index ?? null,
    model: choice?.model || '',
  };
}

/** The ModelChoice a draft stores, or null until it names a model. */
function choiceFrom(draft) {
  const model = draft.model.trim();
  if (!draft.provider || !model) return null;
  return {
    provider: draft.provider,
    config_index: draft.configIndex > 1 ? draft.configIndex : null,
    model,
  };
}

/** Whether two stored choices (or nulls) pick the same model. */
function sameChoice(a, b) {
  if (!a || !b) return !a && !b;
  const keyOf = (c) => (c.config_index > 1 ? c.config_index : null);
  return a.provider === b.provider
    && keyOf(a) === keyOf(b)
    && a.model === b.model;
}

/**
 * Label a stored choice whose key isn't offered, saying what a run does.
 *
 * A run uses the chosen key when it's saved, else the provider's first
 * saved key, as every model on the platform does (`pick_provider_config`).
 * So a chosen key that's saved but not offered (it can't be read, or a
 * local config has no endpoint) refuses the run when it starts, as does a
 * first key that can't serve a model.
 *
 * @param {object} draft - The picker's draft.
 * @param {object} [providerConfigs] - Raw `provider_configs`.
 * @returns {string}
 */
function missingKeyLabel(draft, providerConfigs) {
  const name = PROVIDER_LABELS[draft.provider] || draft.provider;
  const index = draft.configIndex || 1;
  const saved = providerConfigs?.[draft.provider];
  const configs = Array.isArray(saved) ? saved : [];
  if (configs.some((cfg) => (cfg?.config_index ?? 1) === index)) {
    return `${name} (key ${index} can't be used — check it in Settings)`;
  }
  if (!isUsableProviderConfig(draft.provider, configs[0])) {
    return `${name} (key not found — add it in Settings)`;
  }
  return `${name} (key ${index} not found — runs on your first ${name} key)`;
}

/** The combined provider-and-key select value, e.g. "openai_2". */
function selectValueOf(draft) {
  if (!draft.provider) return '';
  return draft.configIndex && draft.configIndex > 1
    ? `${draft.provider}_${draft.configIndex}`
    : draft.provider;
}

/**
 * Picker for one of a node's model slots: an Extractor's model, or an
 * agent node's summarizer step.
 *
 * The first option leaves the slot on the profile default, labelled with
 * the model it resolves to (served by GET /workflows/model-slots, so it is
 * the model a run uses).  Picking a provider and a model stores a
 * ModelChoice, `{provider, config_index, model}`.  A choice is stored only
 * once it names a model; until then the slot stays on the profile default,
 * and the picker says so, so the form never shows a model that won't run.
 *
 * @param {object}   props
 * @param {string}   props.label            - The picker's label.
 * @param {string}   [props.hint]           - A note under the picker.
 * @param {object}   [props.value]          - The stored ModelChoice, or null.
 * @param {Function} props.onChange         - Called with a ModelChoice, or
 *   null for the profile default.
 * @param {object}   [props.defaultChoice]  - The slot's served default.
 * @param {object}   [props.providerConfigs] - Raw `provider_configs`.
 * @param {object}   [props.modelLists]     - What model fields list
 *   (useModelSettings().modelLists): the same list Settings and the left
 *   panel show.
 * @param {string}   props.idPrefix         - Unique prefix for element ids.
 */
export default function ModelChoicePicker({
  label,
  hint,
  value,
  onChange,
  defaultChoice,
  providerConfigs,
  modelLists = {},
  idPrefix,
}) {
  const [draft, setDraft] = useState(() => draftFrom(value));

  /* Follow changes made outside the picker (undo, redo).  A draft that
     already stores `value` stays as typed, so a provider the user is still
     choosing a model for, or a model name mid-edit, isn't reset. */
  useEffect(() => {
    setDraft((current) =>
      sameChoice(choiceFrom(current), value) ? current : draftFrom(value),
    );
  }, [value]);

  const options = useMemo(
    () => [
      STACKFORM_PROVIDER_OPTION,
      ...buildConfiguredProviderOptions(providerConfigs),
    ],
    [providerConfigs],
  );
  const selectValue = selectValueOf(draft);
  const missingOption =
    selectValue && !options.some((opt) => opt.value === selectValue);

  /* Store the draft's choice, unless the slot already holds it. */
  const commit = (next) => {
    setDraft(next);
    const choice = choiceFrom(next);
    if (!sameChoice(choice, value)) onChange(choice);
  };

  const handleProviderChange = (selected) => {
    if (!selected) {
      commit(draftFrom(null));
      return;
    }
    const opt = options.find((o) => o.value === selected);
    if (!opt) return;
    commit({
      provider: opt.provider,
      configIndex: opt.configIndex ?? 1,
      model: initialModel(opt, modelLists),
    });
  };

  return (
    <Field label={label}>
      <select
        className="wf-select"
        aria-label={label}
        value={selectValue}
        onChange={(e) => handleProviderChange(e.target.value)}
      >
        <option value="">{defaultChoiceLabel(defaultChoice)}</option>
        {options.map(({ value: optValue, label: optLabel }) => (
          <option key={optValue} value={optValue}>
            {optLabel}
          </option>
        ))}
        {missingOption && (
          <option value={selectValue}>
            {missingKeyLabel(draft, providerConfigs)}
          </option>
        )}
      </select>
      {draft.provider && (
        <>
          <ModelSelect
            providerKey={selectValue}
            value={draft.model}
            onChange={(model) => commit({ ...draft, model })}
            modelLists={modelLists}
            ariaLabel={`${label} name`}
            listId={`${idPrefix}-models`}
            selectProps={{ className: 'wf-select', style: { marginTop: 6 } }}
            inputProps={{ className: 'wf-input', style: { marginTop: 6 } }}
          />
          {!draft.model.trim() && (
            <FieldAlert>
              Choose a model to use this provider. Until then, this node
              uses the profile default.
            </FieldAlert>
          )}
        </>
      )}
      <TypeHint text={hint} />
    </Field>
  );
}
